import Image from 'next/image';
import { oneLine } from 'common-tags';

import { NextLink } from '@/atoms';

import { ConditionalWrapper } from '@/components/hoc';
import { InitPropsProvider } from '@/components/providers/initPropsProvider';
import Column from '../Column';

import type { FunctionComponent } from 'react';
import type { FlyOutProps } from './types';

/**
 * Primary UI component for user interaction
 */
export const FlyOut: FunctionComponent<FlyOutProps> = ({
  open,
  setOpen,
}: FlyOutProps) => {
  let timeOut: NodeJS.Timeout;

  return (
    <nav
      className="gap-6 lg:flex lg:w-full lg:items-start lg:justify-between"
      onMouseLeave={() => setOpen(-1)}
    >
      <InitPropsProvider.Consumer>
        {({
          menus: {
            primaryMenu: { menus },
          },
        }) =>
          // TODO: Añadir tipos
          menus?.map((category: any, index: number) => (
            <div
              key={index}
              className="flex h-16 items-center justify-center p-4"
              onMouseEnter={() => {
                if (!category.columnsNumber) setOpen(-1);

                if (open !== -1) {
                  if (category.columnsNumber) setOpen(index);
                  return;
                }

                timeOut = setTimeout(() => {
                  if (category.columnsNumber) setOpen(index);
                }, 400);
              }}
              onMouseLeave={() => {
                clearTimeout(timeOut);
              }}
            >
              <div className="u-actions u-actions--nav relative z-10 flex items-center">
                <ConditionalWrapper
                  condition={category.parentMenuLink}
                  wrapper={(children) => (
                    <NextLink href={category.parentMenuLink.url}>
                      <a>{children}</a>
                    </NextLink>
                  )}
                >
                  {category.parentMenu}
                </ConditionalWrapper>
              </div>

              {category.columnsNumber && (
                <div
                  className={oneLine`
                  absolute inset-x-0 top-[calc(100%-64px)] w-full transition-opacity
                  ${
                    open === index
                      ? 'opacity-100'
                      : 'pointer-events-none opacity-0'
                  }
                `}
                >
                  <div className="pt-16 text-sm text-gray-500">
                    {/* Presentational element used to render the bottom shadow, if we put the shadow on the actual panel it pokes out the top, so we use this shorter element to hide the top of the shadow */}
                    <div
                      className="absolute inset-0 top-1/2 bg-primary-50 shadow"
                      aria-hidden="true"
                    />

                    <div className="relative z-50 bg-primary-50">
                      <div className="u-wrapper border-t border-primary-100 px-4">
                        <div className="grid grid-flow-col gap-x-8 gap-y-10 py-16">
                          {/* FEATURED */}
                          {category.menuImageActive ? (
                            <div className="col-start-2 grid max-w-[250px] grid-flow-col gap-x-8">
                              {category.imageLink ? (
                                <NextLink href={category.imageLink.url}>
                                  <a
                                    aria-label={
                                      category.image.altText ||
                                      'Imagen destacada'
                                    }
                                  >
                                    <Image
                                      src={category.image.sourceUrl}
                                      width={category.image.mediaDetails.width}
                                      height={
                                        category.image.mediaDetails.height
                                      }
                                      alt={category.image.altText}
                                    />
                                  </a>
                                </NextLink>
                              ) : (
                                <div>
                                  <Image
                                    src={category.image.sourceUrl}
                                    width={category.image.mediaDetails.width}
                                    height={category.image.mediaDetails.height}
                                    alt={category.image.altText}
                                  />
                                </div>
                              )}
                            </div>
                          ) : null}

                          {/* COLUMNS */}
                          <div className="row-start-1 grid grid-flow-col gap-x-8 gap-y-10 pr-[150px] text-sm">
                            {category.column &&
                              category.column.map(
                                // TODO: Añadir tipos
                                (column: any, columnIndex: number) => (
                                  <Column
                                    column={column}
                                    key={columnIndex}
                                    parentMenuName={category.parentMenu}
                                  />
                                ),
                              )}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          ))
        }
      </InitPropsProvider.Consumer>
    </nav>
  );
};
